import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UploadSimple, MagnifyingGlass, Trash, Sparkle, User } from "@phosphor-icons/react";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { Input } from "../../components/ui/input";

export default function Resumes() {
  const [resumes, setResumes] = useState([]);
  const [q, setQ] = useState("");
  const [uploading, setUploading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [aiResults, setAiResults] = useState(null);
  const fileRef = useRef(null);

  const load = async (query = "") => {
    setAiResults(null);
    const { data } = await api.get(`/hr/resumes${query ? `?q=${encodeURIComponent(query)}` : ""}`);
    setResumes(data);
  };
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    setUploading(true);
    try {
      await api.post("/hr/resumes/upload", fd, { headers: { "Content-Type":"multipart/form-data" } });
      toast.success("Resume parsed & indexed");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); e.target.value = ""; }
  };

  const aiSearch = async () => {
    if (!q.trim()) { load(); return; }
    setSearching(true);
    try {
      const { data } = await api.post("/hr/resumes/ai-search", { query: q });
      setAiResults(data);
      if (data.length === 0) toast.message("No strong matches · falling back to keyword search");
      else toast.success(`Found ${data.length} match${data.length === 1 ? "" : "es"}`);
    } catch (err) {
      toast.error("AI search failed · using keyword search");
      load(q);
    } finally { setSearching(false); }
  };

  const keywordSearch = () => load(q);

  const remove = async (id) => {
    if (!window.confirm("Delete this resume?")) return;
    await api.delete(`/hr/resumes/${id}`); toast.success("Deleted"); load(q);
  };

  const list = aiResults ?? resumes;

  return (
    <Layout title="Resume Repository" subtitle="AI-parsed · skills + name + location search"
      actions={
        <>
          <input ref={fileRef} type="file" accept=".pdf,.docx" className="hidden" onChange={upload} data-testid="resume-upload-input"/>
          <button onClick={()=>fileRef.current?.click()} disabled={uploading} data-testid="upload-resume-button" className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C] disabled:opacity-50">
            <UploadSimple size={16} weight="bold"/> {uploading?"Parsing…":"Upload Resume"}
          </button>
        </>
      }>
      <div className="flex flex-wrap items-center gap-3 mb-6 border border-black/10 bg-white p-4">
        <MagnifyingGlass size={18} className="text-[#D90429]"/>
        <Input value={q} onChange={e=>setQ(e.target.value)} placeholder='Try: "React + Mumbai 3+ yrs" or "Sales Engineer Delhi"' className="rounded-none flex-1 min-w-[280px] border-black/15" data-testid="search-input" onKeyDown={e=>e.key==="Enter"&&aiSearch()}/>
        <button onClick={aiSearch} disabled={searching} data-testid="ai-search-button" className="inline-flex items-center gap-2 px-4 py-2 bg-[#0A0A0A] text-white text-sm font-semibold hover:bg-[#D90429] disabled:opacity-50">
          <Sparkle size={14} weight="bold"/> {searching?"Ranking…":"AI Match"}
        </button>
        <button onClick={keywordSearch} className="px-3 py-2 border border-black/15 text-sm" data-testid="keyword-search-button">Keyword</button>
        {(q || aiResults) && <button onClick={()=>{setQ(""); load();}} className="text-xs text-[#717680] hover:text-[#0A0A0A]" data-testid="reset-search">Reset</button>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.length===0 && <div className="col-span-3 px-4 py-16 text-center text-[#717680] border border-dashed border-black/15 bg-white">No resumes {q?"match your query":"yet"}. Upload a PDF or DOCX to start.</div>}
        {list.map(r => (
          <div key={r.id} className="border border-black/10 bg-white p-4 hover:border-[#D90429] hover:shadow-[0_20px_40px_-20px_rgba(217,4,41,0.25)] transition-all" data-testid={`resume-card-${r.id}`}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="font-display text-base font-bold text-[#0A0A0A]">{r.candidate_name}</div>
                <div className="text-[11px] text-[#717680] font-mono-shelv mt-0.5">{r.current_role || "—"}{r.location ? ` · ${r.location}` : ""}</div>
              </div>
              {r.match_score !== undefined && (
                <span className="text-[10px] font-mono-shelv tracking-widest px-1.5 py-0.5 border border-[#D90429]/40 text-[#D90429] bg-[#FFE5E9]/40">{r.match_score}</span>
              )}
            </div>
            {r.match_reason && <div className="text-[11px] text-[#D90429] italic mb-2">{r.match_reason}</div>}
            <div className="text-xs text-[#4A4D57] line-clamp-2 mb-2">{r.summary || "—"}</div>
            <div className="flex flex-wrap gap-1 mb-2">
              {(r.skills || []).slice(0,8).map(s => <span key={s} className="text-[10px] bg-[#FAFBFC] border border-black/10 px-1.5 py-0.5">{s}</span>)}
            </div>
            <div className="text-[10px] text-[#717680] font-mono-shelv flex items-center justify-between border-t border-black/5 pt-2">
              <span><User size={10} weight="bold" className="inline mr-1"/>{r.total_experience_years || 0} yrs · {r.email || "—"}</span>
              <button onClick={()=>remove(r.id)} className="text-[#D90429] hover:bg-[#D90429]/10 p-1" data-testid={`del-resume-${r.id}`}><Trash size={11}/></button>
            </div>
          </div>
        ))}
      </div>
    </Layout>
  );
}
